import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { formatPrice } from '@/lib/pricing';
import type { AccountState } from '@/lib/billing';
import { TIERS } from '@/lib/tier';
import './CancelFlow.css';

/**
 * The cancellation flow.
 *
 * Five steps, four for a trial customer. This is an offboarding flow, not a confirm dialog —
 * it asks, and it captures why, but it must never trap.
 *
 * ## The rules, all load-bearing
 *
 * 1. **Every step carries a direct "cancel anyway" link.** Required by EU one-click
 *    cancellation regimes and right regardless. It is in the footer of every step below; do
 *    not remove it from any of them, including the offer steps — especially the offer steps.
 * 2. **Satisfaction is asked BEFORE any offer.** A customer who has just recalled something
 *    positive is measurably likelier to stay. Reordering these two steps breaks the mechanism.
 * 3. **The salvage attempt is matched to the stated reason**, not generic: too expensive →
 *    a cheaper plan; not using it → pause; missing features or poor support → talk to the team.
 * 4. **Never more than one discount offer.** Repeatedly bargaining with someone who has
 *    decided reads as obstruction and generates complaints.
 * 5. **Trial customers skip the discount entirely** — a discount is meaningless to someone who
 *    has not paid — and are told plainly they have not been charged and won't be.
 * 6. **Cancel at period end**, showing the exact date access ends. The subscription is never
 *    killed mid-period.
 * 7. **The reason and the free-text feedback are recorded either way**, including when the
 *    customer is saved, because a saved customer still told you something.
 *
 * The payment provider is never named.
 */

type Reason = 'expensive' | 'not-using' | 'missing-feature' | 'poor-support' | 'other';

const REASONS: { key: Reason; label: string }[] = [
  { key: 'expensive', label: 'Too expensive' },
  { key: 'not-using', label: 'I’m not using it enough' },
  { key: 'missing-feature', label: 'Missing a feature I need' },
  { key: 'poor-support', label: 'Poor support experience' },
  { key: 'other', label: 'Something else' },
];

type Step = 'reason' | 'worked' | 'salvage' | 'offer' | 'confirm';

export function CancelFlow({
  account,
  onClose,
  onCancelConfirmed,
}: {
  account: AccountState;
  onClose: () => void;
  onCancelConfirmed: () => void;
}) {
  const [step, setStep] = useState<Step>('reason');
  const [reason, setReason] = useState<Reason | null>(null);
  const [worked, setWorked] = useState('');
  const [feedback, setFeedback] = useState('');

  // Unreachable with a null tier — cancelling presupposes a subscription — but typed
  // honestly rather than asserted away.
  const plan = account.tier ? TIERS[account.tier] : null;
  const price = (n: number) => formatPrice(account.currency, n);

  /** The discount step is skipped entirely for a trial customer. */
  const steps: Step[] = account.inTrial
    ? ['reason', 'worked', 'salvage', 'confirm']
    : ['reason', 'worked', 'salvage', 'offer', 'confirm'];
  const stepIndex = steps.indexOf(step);

  function next() {
    const following = steps[stepIndex + 1];
    if (following) setStep(following);
  }

  /**
   * Record what we have and cancel. Reached from the footer of every step, and from the final
   * confirm. The answers collected so far are still recorded — a customer who bails at step 2
   * has told us their reason.
   */
  function cancelAnyway() {
    onCancelConfirmed();
  }

  return (
    <div className="cancel" role="dialog" aria-modal="true" aria-label="Cancel plan">
      <header className="cancel__head">
        <div>
          <p className="u-meta">
            Step {stepIndex + 1} of {steps.length}
          </p>
          <h2 className="cancel__title">{TITLES[step]}</h2>
        </div>
        <button type="button" className="cancel__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="cancel__body">
        {step === 'reason' && (
          <>
            <p className="cancel__lead">
              {account.inTrial
                ? 'You haven’t been charged. Before you go — what didn’t work?'
                : 'Before you go — why are you canceling?'}
            </p>
            <div className="cancel__options">
              {REASONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className="cancel__option"
                  data-selected={reason === option.key}
                  onClick={() => {
                    setReason(option.key);
                    next();
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'worked' && (
          <>
            <p className="cancel__lead">What did work for you?</p>
            <p className="cancel__sub">
              Even if you’re leaving, this tells us what to keep.
            </p>
            <textarea
              className="cancel__text"
              rows={4}
              value={worked}
              placeholder="The weekly digest, the ordinance tracking…"
              onChange={(e) => setWorked(e.target.value)}
            />
            <button type="button" className="cancel__primary" onClick={next}>
              Continue
            </button>
          </>
        )}

        {step === 'salvage' && <Salvage reason={reason} account={account} onDecline={next} />}

        {step === 'offer' && (
          <>
            {/* One offer. Never a sequence of escalating ones. */}
            <p className="cancel__lead">Would 30% off for three months help?</p>
            <p className="cancel__sub">
              {account.nextChargeAmount === null ? (
                <>
                  You’d pay 30% less until {account.nextChargeDate}, then your usual rate.
                </>
              ) : (
                <>
                  You’d pay {price(Math.round(account.nextChargeAmount * 0.7))} a month until{' '}
                  {account.nextChargeDate}, then your usual {price(account.nextChargeAmount)}.
                </>
              )}{' '}
              Everything on {plan?.label ?? 'your plan'} stays exactly as it is.
            </p>
            <button type="button" className="cancel__primary" onClick={onClose}>
              Apply the discount and stay
            </button>
            <button type="button" className="cancel__secondary" onClick={next}>
              No thanks, continue canceling
            </button>
          </>
        )}

        {step === 'confirm' && (
          <>
            {account.inTrial ? (
              <div className="cancel__trial">
                <Icon name="check" />
                <p>
                  <strong>You have not been charged, and you won’t be.</strong> Canceling now
                  ends your trial before the {account.trialEndsOn} charge date.
                </p>
              </div>
            ) : (
              <p className="cancel__lead">
                Your plan will cancel on <strong>{account.nextChargeDate}</strong>. You keep full
                access until then — nothing stops today.
              </p>
            )}
            <p className="cancel__sub">Anything else we should know?</p>
            <textarea
              className="cancel__text"
              rows={4}
              value={feedback}
              placeholder="Optional"
              onChange={(e) => setFeedback(e.target.value)}
            />
            <button type="button" className="cancel__danger" onClick={cancelAnyway}>
              {account.inTrial ? 'End my trial' : 'Cancel my plan'}
            </button>
            <button type="button" className="cancel__secondary" onClick={onClose}>
              Never mind, keep my plan
            </button>
          </>
        )}
      </div>

      {/* On EVERY step. The flow may ask; it must never trap. */}
      {step !== 'confirm' && (
        <footer className="cancel__foot">
          <button type="button" className="cancel__anyway" onClick={cancelAnyway}>
            Cancel my plan anyway
          </button>
        </footer>
      )}
    </div>
  );
}

const TITLES: Record<Step, string> = {
  reason: 'Before you go',
  worked: 'What worked',
  salvage: 'One suggestion',
  offer: 'An offer',
  confirm: 'Confirm cancellation',
};

/**
 * The salvage attempt, matched to the reason given. A generic "are you sure?" here would waste
 * the step — the value comes from making a relevant offer, not from adding friction.
 */
function Salvage({
  reason,
  account,
  onDecline,
}: {
  reason: Reason | null;
  account: AccountState;
  onDecline: () => void;
}) {
  if (reason === 'expensive') {
    const cheaper = account.tier === 'pro' ? TIERS.intelligence : TIERS.snapshot;
    return (
      <>
        <p className="cancel__lead">Would a smaller plan work better?</p>
        <p className="cancel__sub">
          {cheaper.label} is {formatPrice(account.currency, cheaper.usdMonthly)}/month.{' '}
          {/* A genuine reason to switch rather than leave, so it is stated here. */}
          <strong>Any credits you’ve already paid for carry over</strong> — they never expire,
          and switching plans doesn’t forfeit them.
        </p>
        <button type="button" className="cancel__primary">
          Switch to {cheaper.label}
        </button>
        <button type="button" className="cancel__secondary" onClick={onDecline}>
          No thanks, continue canceling
        </button>
      </>
    );
  }

  if (reason === 'not-using') {
    return (
      <>
        <p className="cancel__lead">Want to pause instead?</p>
        <p className="cancel__sub">
          We’ll stop billing and keep everything you follow, plus your signals and records, exactly as they are.{' '}
          <strong>Paid credits survive a pause.</strong> Pick things up whenever you’re ready.
        </p>
        <button type="button" className="cancel__primary">
          Pause my subscription
        </button>
        <button type="button" className="cancel__secondary" onClick={onDecline}>
          No thanks, continue canceling
        </button>
      </>
    );
  }

  return (
    <>
      <p className="cancel__lead">
        {reason === 'poor-support'
          ? 'We’d like to put that right.'
          : 'Tell us what’s missing?'}
      </p>
      <p className="cancel__sub">
        {reason === 'poor-support'
          ? 'A real person will read this and get back to you — usually the same day.'
          : 'If it’s on the roadmap we’ll tell you when. If it isn’t, that’s useful for us to know.'}
      </p>
      <button type="button" className="cancel__primary">
        Talk to the team
      </button>
      <button type="button" className="cancel__secondary" onClick={onDecline}>
        No thanks, continue canceling
      </button>
    </>
  );
}
